import React, { useState, useEffect } from 'react';

interface PublishConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (seoData?: {
    seo_title: string;
    seo_description: string;
    seo_keywords: string;
    public_slug: string;
    custom_url?: string;
  }) => void;
  isPublishing: boolean; // true for publish, false for unpublish
  matchName?: string;
  existingData?: {
    seo_title?: string;
    seo_description?: string;
    seo_keywords?: string;
    public_slug?: string;
    custom_url?: string;
  };
}

const PublishConfirmModal: React.FC<PublishConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isPublishing,
  matchName,
  existingData
}) => {
  const [seoData, setSeoData] = useState({
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    public_slug: '',
    custom_url: ''
  });

  const [validationErrors, setValidationErrors] = useState<{
    seo_title?: string;
    seo_description?: string;
    public_slug?: string;
  }>({});

  // Load existing data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear validation errors when modal opens
      setValidationErrors({});
      
      if (existingData) {
        setSeoData({
          seo_title: existingData.seo_title || '',
          seo_description: existingData.seo_description || '',
          seo_keywords: existingData.seo_keywords || '',
          public_slug: existingData.public_slug || '',
          custom_url: existingData.custom_url || ''
        });
      } else if (isPublishing) {
        // Reset form for new publishing
        setSeoData({
          seo_title: '',
          seo_description: '',
          seo_keywords: '',
          public_slug: '',
          custom_url: ''
        });
      }
    }
  }, [isOpen, existingData, isPublishing]);

  const handleInputChange = (field: keyof typeof seoData, value: string) => {
    setSeoData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleConfirm = () => {
    if (isPublishing) {
      // For publishing, validate required fields
      const errors: typeof validationErrors = {};
      
      if (!seoData.seo_title.trim()) {
        errors.seo_title = 'SEO Title is required';
      }
      
      if (!seoData.seo_description.trim()) {
        errors.seo_description = 'SEO Description is required';
      }

      if (!seoData.public_slug.trim()) {
        errors.public_slug = 'Public Slug is required';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Clear any existing errors
      setValidationErrors({});
      onConfirm(seoData);
    } else {
      // For unpublishing, just confirm
      onConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isPublishing ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              <svg 
                className={`w-6 h-6 ${isPublishing ? 'text-green-600' : 'text-yellow-600'}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                {isPublishing ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isPublishing ? 'text-green-900' : 'text-yellow-900'}`}>
                {isPublishing ? 'Publish Match' : 'Unpublish Match'}
              </h3>
              {matchName && (
                <p className="text-sm text-gray-600 mt-1">Match: {matchName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isPublishing ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Configure the SEO settings for this match before publishing to the public.
              </p>
              
              {/* SEO Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SEO Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={seoData.seo_title}
                  onChange={(e) => handleInputChange('seo_title', e.target.value)}
                  placeholder="e.g., Arsenal vs Barcelona 2-1 | Match Report"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                    validationErrors.seo_title 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                  maxLength={255}
                />
                {validationErrors.seo_title ? (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.seo_title}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    This will be used for both search engines and social media sharing
                  </p>
                )}
              </div>

              {/* SEO Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SEO Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={seoData.seo_description}
                  onChange={(e) => handleInputChange('seo_description', e.target.value)}
                  placeholder="e.g., Complete match report, stats, and highlights from Arsenal vs Barcelona"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                    validationErrors.seo_description 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                  rows={3}
                  maxLength={500}
                />
                {validationErrors.seo_description ? (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.seo_description}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {seoData.seo_description.length}/500 characters
                  </p>
                )}
              </div>

              {/* SEO Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SEO Keywords
                </label>
                <input
                  type="text"
                  value={seoData.seo_keywords}
                  onChange={(e) => handleInputChange('seo_keywords', e.target.value)}
                  placeholder="e.g., Arsenal, Barcelona, football, match report, stats, highlights"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated keywords for better search visibility
                </p>
              </div>

              {/* Public Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Public Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={seoData.public_slug || ''}
                  onChange={(e) => handleInputChange('public_slug', e.target.value)}
                  placeholder="e.g., arsenal-vs-barcelona-sep-25-2025"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                    validationErrors.public_slug 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                  maxLength={255}
                />
                {validationErrors.public_slug ? (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.public_slug}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    URL-friendly slug for the match (lowercase, hyphens only)
                  </p>
                )}
              </div>

              {/* Custom URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom URL (Optional)
                </label>
                <input
                  type="text"
                  value={seoData.custom_url}
                  onChange={(e) => handleInputChange('custom_url', e.target.value)}
                  placeholder="e.g., champions-league-final-2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Override the auto-generated URL slug
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                This will remove the match from public visibility. The SEO data will be preserved.
              </p>
              
              {/* Show existing SEO data for reference */}
              {existingData && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium text-gray-700">Current SEO Data:</h4>
                  {existingData.seo_title && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Title:</span>
                      <p className="text-sm text-gray-800">{existingData.seo_title}</p>
                    </div>
                  )}
                  {existingData.seo_description && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Description:</span>
                      <p className="text-sm text-gray-800">{existingData.seo_description}</p>
                    </div>
                  )}
                  {existingData.public_slug && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Public Slug:</span>
                      <p className="text-sm text-gray-800">{existingData.public_slug}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end p-6 bg-gray-50 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isPublishing 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-yellow-600 hover:bg-yellow-700'
            }`}
          >
            {isPublishing ? 'Publish' : 'Unpublish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishConfirmModal;
